import CrossCircleIcon from "@atlaskit/icon/core/cross-circle";
import EditIcon from "@atlaskit/icon/core/edit";
import {useNavigate} from "@tanstack/react-router";
import {OttopilotAdditionalButtonKind, type OttopilotAdditionalButtonsConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {getShouldRenderAdditionalButtonForEvent} from "~/domainModels/eventTemplates";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {buildAdditionalButtonFormUrl} from "~/utilities/additionalButtonFormUrl";
import type {EventWithEventContainer} from "~/utilities/apis/api";

export function EventAdditionalButtonTrigger(props: {
    config: OttopilotAdditionalButtonsConfig;
    event: EventWithEventContainer;
    layout: "dropdownItem" | "tableButton";
    currentUserTemplateIds: ReadonlySet<Uuid>;
    workspaceId: Uuid;
    onOpenModal: (payload: {config: OttopilotAdditionalButtonsConfig; event: EventWithEventContainer}) => void;
    /** When true, marks the row click handler to ignore this click (table grid). */
    shieldTableRowClick?: boolean;
    isDisabled?: boolean;
}) {
    const navigate = useNavigate();

    const visible = getShouldRenderAdditionalButtonForEvent({
        rules: props.config.rules,
        event: props.event,
        currentUserTemplateIds: props.currentUserTemplateIds,
    });
    if (visible === false) {
        return null;
    }

    const runAction = () => {
        if (props.isDisabled === true) {
            return;
        }
        if (props.config.formUrl !== undefined && props.config.formUrl !== "") {
            const href = buildAdditionalButtonFormUrl({
                formUrl: props.config.formUrl,
                formUrlQueryParams: props.config.formUrlQueryParams,
                workspaceId: props.workspaceId,
                eventId: props.event.id,
                eventContainerId: props.event.eventContainer.id,
                eventTemplateId: props.event.templateId,
                buttonKind: props.config.buttonKind,
            });
            navigate({to: href});
            return;
        }
        const hasModal =
            props.config.showModal === true &&
            props.config.formColumnData !== undefined &&
            props.config.formColumnData.length > 0;
        if (hasModal === true) {
            props.onOpenModal({
                config: props.config,
                event: props.event,
            });
        }
    };

    const iconBefore =
        props.config.buttonKind === OttopilotAdditionalButtonKind.cancel ? (
            <CrossCircleIcon label="" />
        ) : (
            <EditIcon label="" />
        );

    if (props.layout === "dropdownItem") {
        return (
            <AnankeDropdownItem
                elemBefore={iconBefore}
                isDisabled={props.isDisabled === true}
                onClick={() => {
                    runAction();
                }}
            >
                {props.config.label}
            </AnankeDropdownItem>
        );
    }

    const appearance =
        props.config.buttonKind === OttopilotAdditionalButtonKind.cancel ? "danger" : "default";

    return (
        <AnankeButton
            type="button"
            appearance={appearance}
            isDisabled={props.isDisabled === true}
            onClick={(e) => {
                if (props.shieldTableRowClick === true) {
                    e.nativeEvent.stopPropagation();
                    e.nativeEvent.preventDefault();
                    const native = e.nativeEvent as MouseEvent & {preventOnRowClicked?: boolean};
                    native.preventOnRowClicked = true;
                }
                runAction();
            }}
        >
            {props.config.label}
        </AnankeButton>
    );
}
